import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { movelAPI, formatarErroAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Smartphone, Pencil, Eye, AlertTriangle, Loader2, ExternalLink } from 'lucide-react';
import { LinhaMovelDialog, StatusLinhaBadge } from '@/components/movel/LinhaMovelDialog';

const PERFIS_EDITAM = ['ADMIN', 'BKO', 'BKO_MAILING', 'GERENTE'];

export const LinhasMoveisVenda = ({ vendaId, temMovel }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const podeEditar = PERFIS_EDITAM.includes(user?.perfil);
  const [linhas, setLinhas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selecionada, setSelecionada] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    if (!vendaId || !temMovel) { setLoading(false); return; }
    try {
      const r = await movelAPI.daVenda(vendaId);
      setLinhas(r.data?.linhas || []);
    } catch { setLinhas([]); } finally { setLoading(false); }
  }, [vendaId, temMovel]);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async (form) => {
    if (!selecionada) return;
    setSalvando(true);
    try {
      const r = await movelAPI.atualizar(selecionada.id, form);
      toast.success(r.data?.message === 'Nenhuma alteração' ? 'Nenhuma alteração para salvar' : 'Linha atualizada com sucesso!');
      setSelecionada(null);
      carregar();
    } catch (error) {
      toast.error(formatarErroAPI(error), { duration: 6000 });
    } finally { setSalvando(false); }
  };

  if (!temMovel) return null;
  const ativas = linhas.filter((l) => l.status_linha === 'Ativo').length;
  const pendentes = linhas.filter((l) => l.dias_pendente != null).length;

  return (
    <Card data-testid="card-linhas-moveis">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <Smartphone className="h-5 w-5 text-purple-500" />
          Linhas móveis ({linhas.length})
          {linhas.length > 0 && <Badge variant="outline" className="text-emerald-500 border-emerald-500/40" data-testid="linhas-ativas-count">{ativas} ativa{ativas !== 1 ? 's' : ''}</Badge>}
          {pendentes > 0 && <Badge className="bg-amber-500 text-black border-0" data-testid="linhas-pendentes-count"><AlertTriangle className="h-3 w-3 mr-1" />{pendentes} pendente{pendentes > 1 ? 's' : ''}</Badge>}
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={() => navigate('/movel')} data-testid="btn-ir-aba-movel"><ExternalLink className="h-4 w-4 mr-1" /> Aba Móvel</Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-6 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-purple-500" /></div>
        ) : linhas.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="linhas-moveis-vazio">Nenhuma linha móvel gerada para esta venda.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tabela-linhas-moveis">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="p-2">Linha</th><th className="p-2">Tipo</th><th className="p-2">Número</th><th className="p-2">Operadora</th>
                  <th className="p-2">ICCID</th><th className="p-2">Telefone</th><th className="p-2">Cód. Aut.</th><th className="p-2">Status</th><th className="p-2 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((l) => (
                  <tr key={l.id} className="border-b border-gray-700/20" data-testid={`linha-venda-${l.indice}`}>
                    <td className="p-2"><Badge variant="outline">{l.indice}/{l.qtd_linhas}</Badge></td>
                    <td className="p-2">{l.tipo_linha}</td>
                    <td className="p-2 whitespace-nowrap">{l.numero_linha || '-'}</td>
                    <td className="p-2">{l.operadora || '-'}</td>
                    <td className="p-2 font-mono text-xs" data-testid={`linha-venda-iccid-${l.indice}`}>{l.iccid || <span className="text-amber-500">sem ICCID</span>}</td>
                    <td className="p-2 whitespace-nowrap" data-testid={`linha-venda-telefone-${l.indice}`}>{l.telefone || '-'}</td>
                    <td className="p-2 font-mono text-xs">{l.codigo_autorizacao || '-'}</td>
                    <td className="p-2">
                      <div className="flex flex-col gap-1 items-start">
                        <StatusLinhaBadge status={l.status_linha} />
                        {l.dias_pendente != null && <Badge className="bg-amber-500 text-black border-0 text-[10px]"><AlertTriangle className="h-3 w-3 mr-1" /> há {l.dias_pendente} dias</Badge>}
                      </div>
                    </td>
                    <td className="p-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => setSelecionada(l)} title={podeEditar ? 'Editar linha' : 'Ver linha'} data-testid={`btn-linha-venda-${l.indice}`}>
                        {podeEditar ? <Pencil className="h-4 w-4 text-purple-400" /> : <Eye className="h-4 w-4 text-purple-400" />}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      <LinhaMovelDialog linha={selecionada} aberto={!!selecionada} onFechar={() => setSelecionada(null)} podeEditar={podeEditar} onSalvar={salvar} salvando={salvando} />
    </Card>
  );
};

export default LinhasMoveisVenda;
